import { Download } from "lucide-react";
import { useEffect, useId, useState, type FC, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { MetaLine } from "@/components/ui/meta-line";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

import type {
  ZipRequest,
  ZipScope,
  ZipTree
} from "../../caseFiles/api/filesystemApi";
import { formatBytes } from "../../caseFiles/fileKind";
import { zipName, type ZipSummary } from "./downloadModel";
import type { ZipDownload } from "./useZipDownload";

type Props = {
  tree: ZipTree;
  /** Rows selected in the tree, files and folders. */
  selectedIds: string[];
  /** What This Folder downloads: the selected folder, or the selected file's. */
  folder: { id: string; name: string } | null;
  /** Files (and, when known, bytes) a request would zip. */
  summarize: (request: ZipRequest) => ZipSummary;
  /** Under Include Filing Packet ("207 pages"). */
  packetHint?: string | null;
  onDownload: (download: ZipDownload) => void;
  /**
   * Opened from elsewhere (a "…" menu): the popover hangs off `anchor` and
   * shows no button of its own.
   */
  anchor?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const ScopeRow: FC<{
  value: ZipScope;
  label: string;
  meta?: string | null;
  disabled?: boolean;
}> = ({ value, label, meta, disabled }) => {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className="flex h-7 cursor-default items-center gap-2 rounded-md px-2 text-base text-fg hover:bg-fill-1 has-disabled:opacity-50 has-disabled:hover:bg-transparent"
    >
      <RadioGroupItem
        id={id}
        value={value}
        disabled={disabled}
        aria-label={label}
      />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {meta ? (
        <span className="max-w-32 shrink-0 truncate text-sm text-fg-3">
          {meta}
        </span>
      ) : null}
    </label>
  );
};

const CheckRow: FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint?: ReactNode;
}> = ({ checked, onChange, label, hint }) => {
  const id = useId();
  return (
    <label htmlFor={id} className="flex cursor-default items-start gap-2 py-1">
      <Checkbox
        id={id}
        className="mt-0.5"
        checked={checked}
        onCheckedChange={(next) => onChange(next === true)}
        aria-label={label}
      />
      <span className="flex min-w-0 flex-col">
        <span className="text-base text-fg">{label}</span>
        {hint ? <span className="text-sm text-fg-3">{hint}</span> : null}
      </span>
    </label>
  );
};

/**
 * Download All for either tree: the header's download button and the popover
 * it opens. Scope (All, Selected, This Folder), Keep Folder Structure, and for
 * Case Files Prefix Exhibit Labels; Work Products adds Drafts As and Include
 * Filing Packet. The footer counts what the zip will hold.
 */
export const DownloadPopover: FC<Props> = ({
  tree,
  selectedIds,
  folder,
  summarize,
  packetHint,
  onDownload,
  anchor,
  open: openProp,
  onOpenChange
}) => {
  const files = tree === "files";
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const [scope, setScope] = useState<ZipScope>("all");
  const [keep, setKeep] = useState(true);
  const [prefix, setPrefix] = useState(true);
  const [draftFormat, setDraftFormat] = useState<"docx" | "pdf">("docx");
  const [withPacket, setWithPacket] = useState(true);

  const selectedCount = selectedIds.length;
  useEffect(() => {
    if (open) setScope(selectedCount > 1 ? "selected" : "all");
  }, [open, selectedCount]);

  const request: ZipRequest = {
    scope,
    ...(scope === "selected" ? { node_ids: selectedIds } : {}),
    ...(scope === "folder" && folder ? { folder_id: folder.id } : {}),
    keep_structure: keep,
    ...(files
      ? { prefix_labels: prefix }
      : { draft_format: draftFormat, include_packet: withPacket })
  };
  const summary = summarize(request);
  const allSummary = summarize({ ...request, scope: "all" });
  const noun = (n: number) =>
    `${n} ${files ? "file" : "item"}${n === 1 ? "" : "s"}`;

  const download = () => {
    setOpen(false);
    onDownload({
      tree,
      request,
      name: zipName(tree, request, {
        count: summary.files,
        folderName: folder?.name
      }),
      total: summary.files
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {anchor ? (
        <PopoverAnchor asChild>{anchor}</PopoverAnchor>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-6"
                aria-label="Download All"
                data-testid={`download-all-${tree}`}
              >
                <Download className="size-3.5" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Download All</TooltipContent>
        </Tooltip>
      )}
      <PopoverContent
        align={anchor ? "end" : "start"}
        className="w-76 gap-0 p-0"
        data-testid="download-popover"
      >
        <div className="flex items-center gap-2 px-3 pt-2.5 pb-1.5">
          <Download className="size-3.5 text-fg-2" />
          <span className="flex-1 text-base font-medium text-fg">
            {files ? "Download Case Files" : "Download Work Products"}
          </span>
        </div>
        <RadioGroup
          value={scope}
          onValueChange={(value) => setScope(value as ZipScope)}
          className="gap-0 px-1 pb-1.5"
          aria-label="What to Download"
        >
          <ScopeRow
            value="all"
            label={files ? "All Case Files" : "All Work Products"}
            meta={noun(allSummary.files)}
          />
          <ScopeRow
            value="selected"
            label={selectedCount ? `Selected (${selectedCount})` : "Selected"}
            meta={selectedCount ? null : "Nothing selected"}
            disabled={!selectedCount}
          />
          <ScopeRow
            value="folder"
            label="This Folder"
            meta={folder?.name ?? "No folder selected"}
            disabled={!folder}
          />
        </RadioGroup>
        <div className="flex flex-col border-t border-line-1 px-3 py-2">
          {files ? null : (
            <div className="flex items-center gap-2 pb-1.5">
              <span className="flex-1 text-base text-fg">Drafts As</span>
              <Tabs
                value={draftFormat}
                onValueChange={(value) =>
                  setDraftFormat(value === "pdf" ? "pdf" : "docx")
                }
              >
                <TabsList variant="segmented" className="w-28">
                  <TabsTrigger value="docx">DOCX</TabsTrigger>
                  <TabsTrigger value="pdf">PDF</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}
          <CheckRow
            checked={keep}
            onChange={setKeep}
            label="Keep Folder Structure"
            hint="Folders as in the tree"
          />
          {files ? (
            <CheckRow
              checked={prefix}
              onChange={setPrefix}
              label="Prefix Exhibit Labels"
              hint="[B.2] Pay Stubs.pdf; unlabeled files keep their name"
            />
          ) : (
            <CheckRow
              checked={withPacket}
              onChange={setWithPacket}
              label="Include Filing Packet"
              hint={packetHint || "Usually most of the size"}
            />
          )}
        </div>
        <div className="flex items-center gap-2 border-t border-line-1 py-2 pr-2 pl-3">
          <MetaLine
            className="flex-1"
            parts={[
              noun(summary.files),
              summary.bytes !== null && summary.files
                ? formatBytes(summary.bytes)
                : null
            ]}
          />
          <Button
            variant="primary"
            size="sm"
            disabled={!summary.files}
            onClick={download}
          >
            <Download />
            Download Zip
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
